/**
 * Deliadore Premium Store Locator - Production JS Engine
 * Features: Live Instant Search, Dynamic City Filters, Real-Time Working Hours Parser,
 * Intersection Observer Animations, Keyboard Accessibility, Zero Dependencies.
 */

(function () {
  'use strict';

  class DeliadoreStoreLocator {
    constructor(wrapper) {
      this.wrapper = wrapper;
      this.grid = this.wrapper.querySelector('.dl-store-grid');
      this.cards = Array.from(this.wrapper.querySelectorAll('.dl-store-card'));
      this.searchInput = this.wrapper.querySelector('.dl-search-input');
      this.clearBtn = this.wrapper.querySelector('.dl-search-clear');
      this.filterGroup = this.wrapper.querySelector('.dl-filter-group');
      this.countDisplay = this.wrapper.querySelector('.dl-count-num');
      this.emptyState = this.wrapper.querySelector('.dl-empty-state');
      this.resetBtn = this.wrapper.querySelector('.dl-reset-btn');
      
      this.activeCity = 'all';
      this.searchQuery = '';

      if (!this.grid || this.cards.length === 0) return;

      this.init();
    }

    init() {
      this.sortCardsByOrder();
      this.generateCityFilters();
      this.calculateAllWorkingHours();
      this.bindEvents();
      this.setupAnimations();
      this.updateDisplay();
    }

    /**
     * Mağazaları Shopify schema'daki 'order' sayısına göre küçükten büyüğe sıralar
     */
    sortCardsByOrder() {
      this.cards.sort((a, b) => {
        const orderA = parseInt(a.getAttribute('data-order') || '0', 10);
        const orderB = parseInt(b.getAttribute('data-order') || '0', 10);
        return orderA - orderB;
      });

      this.cards.forEach(card => this.grid.appendChild(card));
    }

    /**
     * Kartlardaki data-city niteliklerini tarayarak dinamik şehir butonları oluşturur
     */
    generateCityFilters() {
      if (!this.filterGroup) return;

      const cities = new Set();
      this.cards.forEach(card => {
        const city = card.getAttribute('data-city');
        if (city && city.trim() !== '') {
          cities.add(city.trim());
        }
      });

      this.filterGroup.innerHTML = '';

      // Tümü Butonu
      const allBtn = document.createElement('button');
      allBtn.type = 'button';
      allBtn.className = 'dl-filter-btn dl-active';
      allBtn.setAttribute('data-filter', 'all');
      allBtn.textContent = 'Tümü';
      this.filterGroup.appendChild(allBtn);

      // Şehir Butonları (Alfabetik Sıralı)
      const sortedCities = Array.from(cities).sort((a, b) => a.localeCompare(b, 'tr-TR'));
      sortedCities.forEach(city => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'dl-filter-btn';
        btn.setAttribute('data-filter', city);
        btn.textContent = city;
        this.filterGroup.appendChild(btn);
      });
    }

    /**
     * 09:00 - 18:00, 10:00-22:00 gibi formatları okuyup şu anki saatle kıyaslar
     */
    calculateAllWorkingHours() {
      const now = new Date();
      const currentMinutes = now.getHours() * 60 + now.getMinutes();

      this.cards.forEach(card => {
        const hoursText = card.getAttribute('data-hours') || '';
        const badgeEl = card.querySelector('.dl-status-badge');
        
        if (!badgeEl) return;

        // "24 saat" veya benzeri kesintisiz açık ibareleri
        if (hoursText.toLocaleLowerCase('tr-TR').includes('24 saat') || hoursText.toLocaleLowerCase('tr-TR').includes('sürekli açık')) {
          this.setStatus(badgeEl, true, 'Şu anda Açık');
          return;
        }

        // Saat aralığı regex: 09:00 - 18:00 veya 9:30-22:00
        const timeMatch = hoursText.match(/(\d{1,2})[\.:](\d{2})\s*[-–to]\s*(\d{1,2})[\.:](\d{2})/i);

        if (timeMatch) {
          const startHours = parseInt(timeMatch[1], 10);
          const startMins = parseInt(timeMatch[2], 10);
          const endHours = parseInt(timeMatch[3], 10);
          const endMins = parseInt(timeMatch[4], 10);

          const startTotalMinutes = startHours * 60 + startMins;
          let endTotalMinutes = endHours * 60 + endMins;

          // Gece yarısını geçen saatler için (Örn: 10:00 - 02:00)
          if (endTotalMinutes <= startTotalMinutes) {
            endTotalMinutes += 24 * 60;
          }

          let adjustedCurrentMinutes = currentMinutes;
          if (currentMinutes < startTotalMinutes && endTotalMinutes > 24 * 60) {
            adjustedCurrentMinutes += 24 * 60;
          }

          if (adjustedCurrentMinutes >= startTotalMinutes && adjustedCurrentMinutes < endTotalMinutes) {
            this.setStatus(badgeEl, true, 'Şu anda Açık');
          } else {
            this.setStatus(badgeEl, false, 'Şu anda Kapalı');
          }
        } else {
          // Format algılanamazsa statik saat metnini gizlemeden sadece rozeti kaldır
          badgeEl.style.display = 'none';
        }
      });
    }

    setStatus(element, isOpen, text) {
      element.classList.remove('dl-status-loading', 'dl-status-open', 'dl-status-closed');
      element.classList.add(isOpen ? 'dl-status-open' : 'dl-status-closed');
      element.textContent = text;
    }

    bindEvents() {
      // Şehir Filtre Butonları Tıklaması
      if (this.filterGroup) {
        this.filterGroup.addEventListener('click', (e) => {
          const btn = e.target.closest('.dl-filter-btn');
          if (!btn) return;

          this.filterGroup.querySelectorAll('.dl-filter-btn').forEach(b => b.classList.remove('dl-active'));
          btn.classList.add('dl-active');
          
          this.activeCity = btn.getAttribute('data-filter');
          this.updateDisplay();
        });
      }

      // Canlı Arama Girdisi (Anlık Türkçe uyumlu filtreleme)
      if (this.searchInput) {
        this.searchInput.addEventListener('input', (e) => {
          this.searchQuery = e.target.value.toLocaleLowerCase('tr-TR').trim();
          
          if (this.clearBtn) {
            if (this.searchQuery.length > 0) {
              this.clearBtn.classList.remove('dl-hidden');
            } else {
              this.clearBtn.classList.add('dl-hidden');
            }
          }

          this.updateDisplay();
        });

        // Enter tuşunda form submit engelleme
        this.searchInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') e.preventDefault();
        });
      }

      // Arama Temizleme Butonu
      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', () => {
          this.searchInput.value = '';
          this.searchQuery = '';
          this.clearBtn.classList.add('dl-hidden');
          this.searchInput.focus();
          this.updateDisplay();
        });
      }

      // Sonuç Bulunamadı - Sıfırla Butonu
      if (this.resetBtn) {
        this.resetBtn.addEventListener('click', () => {
          this.resetFilters();
        });
      }
    }

    resetFilters() {
      this.activeCity = 'all';
      this.searchQuery = '';

      if (this.searchInput) {
        this.searchInput.value = '';
      }
      if (this.clearBtn) {
        this.clearBtn.classList.add('dl-hidden');
      }
      if (this.filterGroup) {
        this.filterGroup.querySelectorAll('.dl-filter-btn').forEach(b => {
          if (b.getAttribute('data-filter') === 'all') {
            b.classList.add('dl-active');
          } else {
            b.classList.remove('dl-active');
          }
        });
      }

      this.updateDisplay();
    }

    /**
     * Şehir filtresi ve arama kelimesini eş zamanlı uygulayıp arayüzü günceller
     */
    updateDisplay() {
      let visibleCount = 0;

      this.cards.forEach(card => {
        const city = (card.getAttribute('data-city') || '').toLocaleLowerCase('tr-TR');
        const district = (card.getAttribute('data-district') || '').toLocaleLowerCase('tr-TR');
        const name = (card.getAttribute('data-name') || '').toLocaleLowerCase('tr-TR');
        const address = (card.getAttribute('data-address') || '').toLocaleLowerCase('tr-TR');

        // Şehir filtresi kontrolü
        const matchesCity = (this.activeCity === 'all') || (card.getAttribute('data-city') === this.activeCity);

        // Arama sorgusu kontrolü (Store Name, City, District, Address içinde arar)
        const matchesSearch = (this.searchQuery === '') ||
                              name.includes(this.searchQuery) ||
                              city.includes(this.searchQuery) ||
                              district.includes(this.searchQuery) ||
                              address.includes(this.searchQuery);

        if (matchesCity && matchesSearch) {
          card.classList.remove('dl-hidden');
          visibleCount++;
        } else {
          card.classList.add('dl-hidden');
        }
      });

      // Sayaç Güncelleme
      if (this.countDisplay) {
        this.animateCounter(parseInt(this.countDisplay.textContent, 10) || 0, visibleCount);
      }

      // Empty State Gösterimi
      if (this.emptyState) {
        if (visibleCount === 0) {
          this.emptyState.classList.remove('dl-hidden');
          this.grid.classList.add('dl-hidden');
        } else {
          this.emptyState.classList.add('dl-hidden');
          this.grid.classList.remove('dl-hidden');
        }
      }

      // Filtreleme sonrası görünen kartların animasyonunu tetikle
      this.refreshVisibleAnimations();
    }

    animateCounter(start, end) {
      if (start === end) return;
      const duration = 300;
      const startTime = performance.now();

      const step = (currentTime) => {
        const progress = Math.min((currentTime - startTime) / duration, 1);
        const currentVal = Math.floor(progress * (end - start) + start);
        this.countDisplay.textContent = currentVal;

        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          this.countDisplay.textContent = end;
        }
      };

      requestAnimationFrame(step);
    }

    /**
     * Intersection Observer ile kaydırdıkça belirme animasyonu
     */
    setupAnimations() {
      if (!('IntersectionObserver' in window)) {
        this.wrapper.querySelectorAll('.dl-fade-up').forEach(el => el.classList.add('dl-visible'));
        return;
      }

      const observerOptions = {
        root: null,
        rootMargin: '0px 0px -50px 0px',
        threshold: 0.1
      };

      this.observer = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('dl-visible');
            observer.unobserve(entry.target);
          }
        });
      }, observerOptions);

      this.wrapper.querySelectorAll('.dl-fade-up').forEach(el => {
        this.observer.observe(el);
      });
    }

    refreshVisibleAnimations() {
      this.cards.forEach(card => {
        if (!card.classList.contains('dl-hidden') && !card.classList.contains('dl-visible')) {
          if (this.observer) {
            this.observer.observe(card);
          } else {
            card.classList.add('dl-visible');
          }
        }
      });
    }
  }

  // DOM Yüklendiğinde Başlat
  function initStoreLocators() {
    const locators = document.querySelectorAll('.dl-locator-wrapper');
    locators.forEach(wrapper => {
      if (!wrapper.dataset.dlInitialized) {
        new DeliadoreStoreLocator(wrapper);
        wrapper.dataset.dlInitialized = 'true';
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStoreLocators);
  } else {
    initStoreLocators();
  }

  // Shopify Theme Editor Desteği (Section eklendiğinde/değiştiğinde yeniden yükle)
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('.dl-locator-wrapper') || e.target.classList.contains('dl-section-store-locator')) {
      initStoreLocators();
    }
  });

  document.addEventListener('shopify:section:reorder', () => {
    initStoreLocators();
  });
})();